import React from 'react';
import {
  Box,
  Drawer,
  DrawerBody,
  DrawerHeader,
  Text,
  List,
  ListItem,
  ListItemText,
  ListItemLink,
  Divider,
} from '@razorpay/blade/components';

interface FindIpProps {
  isOpen: boolean;
  onDismiss: () => void;
}

const FindEsicIp = ({ isOpen, onDismiss }: FindIpProps): React.ReactElement => {
  return (
    <Drawer isOpen={isOpen} onDismiss={onDismiss} accessibilityLabel="How to find your IP Number?">
      <DrawerHeader title="How to find your IP Number?" />
      <DrawerBody>
        <Box width="100%" backgroundColor="surface.background.gray.intense" overflow="auto">
          <Box display="flex" flexDirection="column" gap="spacing.5">
            {/* Step 1: Visit the ESIC Website */}
            <Box>
              <Text
                size="medium"
                weight="semibold"
                marginBottom="spacing.3"
                color="surface.text.gray.normal">
                Step 1: Visit the ESIC Website
              </Text>
              <List variant="ordered" size="medium">
                <ListItem>
                  <ListItemText color="surface.text.gray.muted">
                    Open your web browser.
                  </ListItemText>
                </ListItem>
                <ListItem>
                  <ListItemText color="surface.text.gray.muted">
                    Go to the official ESIC website:
                    <ListItemLink
                      href="https://www.esic.gov.in"
                      target="_blank"
                      rel="noopener noreferrer">
                      www.esic.gov.in.
                    </ListItemLink>
                  </ListItemText>
                </ListItem>
              </List>
              <Divider marginTop="spacing.5" />
            </Box>

            {/* Step 2: Log in to the ESIC Portal */}
            <Box>
              <Text
                size="medium"
                weight="semibold"
                marginBottom="spacing.3"
                color="surface.text.gray.normal">
                Step 2: Log in to the ESIC Portal
              </Text>
              <List variant="ordered" size="medium">
                <ListItem>
                  <ListItemText color="surface.text.gray.muted">
                    On the homepage, click on the "Insured Person Login" option under the "For
                    Employees" section.
                  </ListItemText>
                </ListItem>
                <ListItem>
                  <ListItemText color="surface.text.gray.muted">
                    If you already have login credentials, enter your Username (your ESIC IP number)
                    and Password.
                  </ListItemText>
                </ListItem>
                <ListItem>
                  <ListItemText color="surface.text.gray.muted">
                    If you don't know your credentials or haven't registered, proceed to the next
                    step.
                  </ListItemText>
                </ListItem>
              </List>
              <Divider marginTop="spacing.5" />
            </Box>

            {/* Step 3: Retrieve Your ESIC IP Number */}
            <Box>
              <Text
                size="medium"
                weight="semibold"
                marginBottom="spacing.3"
                color="surface.text.gray.normal">
                Step 3: Retrieve Your ESIC IP Number
              </Text>
              <List variant="ordered" size="medium">
                <ListItem>
                  <ListItemText color="surface.text.gray.muted">
                    Click on "Sign Up" or "Forgot Username" if you haven't registered or don't
                    remember your login credentials.
                  </ListItemText>
                </ListItem>
                <ListItem>
                  <ListItemText color="surface.text.gray.muted">
                    You will be redirected to a page where you can retrieve your ESIC IP number
                    using the following details:
                  </ListItemText>
                  <List variant="unordered" size="small">
                    <ListItem>
                      <ListItemText color="surface.text.gray.muted">
                        Your registered mobile number
                      </ListItemText>
                    </ListItem>
                    <ListItem>
                      <ListItemText color="surface.text.gray.muted">
                        Email ID (registered with ESIC)
                      </ListItemText>
                    </ListItem>
                    <ListItem>
                      <ListItemText color="surface.text.gray.muted">Date of Birth</ListItemText>
                    </ListItem>
                    <ListItem>
                      <ListItemText color="surface.text.gray.muted">
                        ESIC Registration Number (you can get this from your employer if available)
                      </ListItemText>
                    </ListItem>
                  </List>
                </ListItem>
              </List>
              <Divider marginTop="spacing.5" />
            </Box>

            {/* Step 4: Enter the Necessary Information */}
            <Box>
              <Text
                size="medium"
                weight="semibold"
                marginBottom="spacing.3"
                color="surface.text.gray.normal">
                Step 4: Enter the Necessary Information
              </Text>
              <List variant="ordered" size="medium">
                <ListItem>
                  <ListItemText color="surface.text.gray.muted">
                    Enter your details as prompted, like name, mobile number, date of birth, or
                    Aadhaar number (if applicable).
                  </ListItemText>
                </ListItem>
                <ListItem>
                  <ListItemText color="surface.text.gray.muted">
                    You will receive an OTP (One-Time Password) on your registered mobile number for
                    verification.
                  </ListItemText>
                </ListItem>
              </List>
              <Divider marginTop="spacing.5" />
            </Box>

            {/* Step 5: Retrieve Your IP Number */}
            <Box>
              <Text
                size="medium"
                weight="semibold"
                marginBottom="spacing.3"
                color="surface.text.gray.normal">
                Step 5: Retrieve Your IP Number
              </Text>
              <List variant="ordered" size="medium">
                <ListItem>
                  <ListItemText color="surface.text.gray.muted">
                    Enter the OTP received on your mobile number.
                  </ListItemText>
                </ListItem>
                <ListItem>
                  <ListItemText color="surface.text.gray.muted">
                    After submitting the OTP, your ESIC IP number will be displayed on the screen.
                  </ListItemText>
                </ListItem>
                <ListItem>
                  <ListItemText color="surface.text.gray.muted">
                    Note it down for future reference or take a screenshot.
                  </ListItemText>
                </ListItem>
              </List>
              <Divider marginTop="spacing.5" />
            </Box>

            {/* Alternative Method: Check Your ESIC E-Pehchan Card */}
            <Box>
              <Text
                size="medium"
                weight="semibold"
                marginBottom="spacing.3"
                color="surface.text.gray.normal">
                Alternative Method: Check Your ESIC E-Pehchan Card
              </Text>
              <ListItemText color="surface.text.gray.muted">
                If you are unable to retrieve the ESIC IP number online, you can check it on your
                E-Pehchan Card, which your employer may have issued. The ESIC IP number is printed
                on this card.
              </ListItemText>
              <Divider marginTop="spacing.5" />
            </Box>

            {/* Additional Method: Contact Your Employer */}
            <Box>
              <Text
                size="medium"
                weight="semibold"
                marginBottom="spacing.3"
                color="surface.text.gray.normal">
                Additional Method: Contact Your Employer
              </Text>
              <ListItemText color="surface.text.gray.muted">
                Your employer's HR or Payroll department will also have access to your ESIC details.
                You can ask them for your ESIC IP number if you are unable to find it online.
              </ListItemText>
            </Box>
          </Box>
        </Box>
      </DrawerBody>
    </Drawer>
  );
};

export default FindEsicIp;
